import { useState, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function ProductPhotoCapture({ onAdd, onClose }) {
  const [preview, setPreview]   = useState(null);
  const [file, setFile]         = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult]     = useState(null);   // { name, brand, quantity, unit }
  const [error, setError]       = useState('');
  const fileRef                 = useRef();

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setResult(null);
    setError('');
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(f);
  }

  async function identify() {
    if (!file) return;
    setScanning(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res  = await fetch(`${API}/scan/product`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Identification failed');
      if (!data.name) throw new Error('Could not identify a product. Try a clearer photo.');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setScanning(false);
    }
  }

  function addItem() {
    if (!result) return;
    onAdd([{
      id:         crypto.randomUUID(),
      name:       [result.brand, result.name].filter(Boolean).join(' '),
      quantity:   result.quantity || 1,
      unit:       result.unit || '',
      is_checked: false,
      added_via:  'photo',
    }]);
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="scan-modal">
        <div className="scan-modal-header">
          <span>📷 Identify Product</span>
          <button className="scan-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="scan-modal-body">
          {!result ? (
            <>
              <div
                className="scan-dropzone"
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Product" className="scan-preview-img" />
                ) : (
                  <>
                    <span className="scan-dropzone-icon">📦</span>
                    <p>Tap to take or upload a product photo</p>
                    <p className="scan-dropzone-sub">Point at the front of the packaging</p>
                  </>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])}
              />

              {error && <p className="scan-error">{error}</p>}

              <div className="scan-modal-actions">
                <button className="scan-btn-cancel" onClick={onClose}>Cancel</button>
                <button
                  className="scan-btn-primary"
                  onClick={identify}
                  disabled={!file || scanning}
                >
                  {scanning ? (
                    <><span className="spinner-sm" /> Identifying…</>
                  ) : 'Identify Product'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="ppc-result">
                {preview && <img src={preview} alt="Product" className="ppc-result-img" />}
                <div className="ppc-result-info">
                  {result.brand && <p className="ppc-result-brand">{result.brand}</p>}
                  <p className="ppc-result-name">{result.name}</p>
                  {(result.quantity || result.unit) && (
                    <p className="ppc-result-qty">
                      {result.quantity && result.quantity !== 1 ? `${result.quantity} ` : ''}
                      {result.unit || ''}
                    </p>
                  )}
                </div>
              </div>

              {error && <p className="scan-error">{error}</p>}

              <div className="scan-modal-actions">
                <button className="scan-btn-cancel" onClick={() => { setResult(null); setPreview(null); setFile(null); }}>
                  ← Retake
                </button>
                <button className="scan-btn-primary" onClick={addItem}>
                  Add to list
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
